
import React from "react";
import { Slider } from "@/components/ui/slider";
import StepCard from "../StepCard";
import { useIsMobile } from "@/hooks/use-mobile";

interface ModifierInputProps {
  selectedMethod: "classic" | "yolo";
  modifier: string;
  onModifierChange: (value: string) => void;
  onNext: () => void;
  onPrevious: () => void;
  currentPass?: number;
}

const ModifierInput = ({ selectedMethod, modifier, onModifierChange, onNext, onPrevious, currentPass = 1 }: ModifierInputProps) => {
  const isMobile = useIsMobile();

  const getSliderOptions = () => {
    if (selectedMethod === "classic") {
      return currentPass === 1 
        ? [-20, -15, -10, -5, 0, 5, 10, 15, 20]
        : [-9, -8, -7, -6, -5, -4, -3, -2, -1, 0];
    } else {
      return currentPass === 1 
        ? [-0.05, -0.04, -0.03, -0.02, -0.01, 0, 0.01, 0.02, 0.03, 0.04, 0.05]
        : [-0.04, -0.035, -0.03, -0.025, -0.02, -0.015, -0.01, -0.005, 0, 0.005, 0.01, 0.015, 0.02, 0.025, 0.03, 0.035];
    }
  };

  const sliderOptions = getSliderOptions();
  const zeroIndex = sliderOptions.indexOf(0);
  
  const getCurrentSliderValue = () => {
    if (!modifier) return [zeroIndex];
    const modifierValue = parseFloat(modifier);
    const index = sliderOptions.findIndex(val => val === modifierValue);
    return [index >= 0 ? index : zeroIndex];
  };

  const handleSliderChange = (value: number[]) => {
    const selectedValue = sliderOptions[value[0]];
    onModifierChange(selectedValue.toString());
  };

  const canProceedFromModifier = () => {
    return modifier !== "";
  };

  const getLabel = () => {
    return "Modifier";
  };

  const getCenterLabel = () => {
    // For pass 2, the center value is not 0, so we need to find the actual center value
    if (currentPass === 2) {
      const centerIndex = Math.floor(sliderOptions.length / 2);
      return sliderOptions[centerIndex].toString();
    }
    return "0";
  };

  return (
    <StepCard
      title=""
      onNext={() => canProceedFromModifier() && onNext()}
      onPrevious={onPrevious}
      showPrevious
      nextButtonText={canProceedFromModifier() ? "Calculate" : "Select Value"}
      nextDisabled={!canProceedFromModifier()}
      showNext={true}
    >
      <div className="space-y-6">
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-3">
          <h3 className="text-lg font-semibold text-left text-gray-900 dark:text-white">Select the smoothest modifier</h3>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-base font-semibold">{getLabel()}</label>
              {modifier && (
                <span className="text-lg font-bold">{modifier}</span>
              )}
            </div>
            <div className="space-y-4">
              <Slider
                value={getCurrentSliderValue()}
                onValueChange={handleSliderChange}
                max={sliderOptions.length - 1}
                min={0}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{sliderOptions[0]}</span>
                <span className="font-medium">{getCenterLabel()}</span>
                <span>{sliderOptions[sliderOptions.length - 1]}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <div className="space-y-2">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              • Pick the number of the object with the smoothest looking and feeling top surface.
            </p>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              • There should not be any gaps between the layer lines.
            </p>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              • Ignore the edges and the numbered part.
            </p>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              • If it's hard to choose between two values, pick the higher number.
            </p>
          </div>
        </div>
      </div>
    </StepCard>
  );
};

export default ModifierInput;
